// src/hooks/useMedicalWorkspace.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

export function useMedicalWorkspace(consultaId) {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Estados de Datos
    const [consulta, setConsulta] = useState(null);
    const [historialPaciente, setHistorialPaciente] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estados de UI y Modales
    const [visitaHistoricaSeleccionada, setVisitaHistoricaSeleccionada] = useState(null);
    const [showConfirmFinish, setShowConfirmFinish] = useState(false);
    const [showPostSaveOptions, setShowPostSaveOptions] = useState(false);
    const [showExitOptions, setShowExitOptions] = useState(false);
    const [procesandoEstado, setProcesandoEstado] = useState(false);

    // Inicialización del entorno
    const iniciarEstacion = useCallback(async () => {
        try {
            setLoading(true);
            const resConsulta = await api.get(`/consultas/${consultaId}`);
            const dataConsulta = resConsulta.data || resConsulta;
            setConsulta(dataConsulta);

            const pacienteId = dataConsulta.paciente?.id || dataConsulta.paciente?._id;
            if (pacienteId) {
                const resHistorial = await api.get(`/consultas/paciente/${pacienteId}`);
                const historialOrdenado = (Array.isArray(resHistorial) ? resHistorial : resHistorial.data || [])
                    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
                setHistorialPaciente(historialOrdenado);
            }
        } catch (error) {
            showToast('Error al inicializar la estación clínica', 'error');
            navigate('/agenda');
        } finally {
            setLoading(false);
        }
    }, [consultaId, navigate, showToast]);

    useEffect(() => {
        if (consultaId) iniciarEstacion();
    }, [consultaId, iniciarEstacion]);

    // Mutaciones de estado (Pausa, Salida)
    const handleMutarEstadoConsulta = async (nuevoEstado) => {
        setProcesandoEstado(true);
        try {
            await api.patch(`/consultas/${consultaId}/estado`, { estado: nuevoEstado });
            showToast(`Paciente marcado como: ${nuevoEstado.replace('_', ' ').toUpperCase()}`, 'info');
            navigate('/dashboard');
        } catch (error) {
            showToast('Error al comunicar con la agenda.', 'error');
        } finally {
            setProcesandoEstado(false);
        }
    };

    // Sellado Legal e Inmutable
    const handleFinalizarYSellar = async () => {
        setProcesandoEstado(true);
        try {
            await api.patch(`/consultas/${consultaId}/estado`, { estado: 'completada' });
            setConsulta(prev => ({ ...prev, estado: 'completada' }));
            setShowConfirmFinish(false);
            setShowPostSaveOptions(true);
            showToast('Historial sellado correctamente', 'success');
        } catch (error) {
            showToast('Error al intentar sellar la consulta legalmente', 'error');
        } finally {
            setProcesandoEstado(false);
        }
    };

    return {
        consulta, historialPaciente, loading,
        visitaHistoricaSeleccionada, setVisitaHistoricaSeleccionada,
        showConfirmFinish, setShowConfirmFinish,
        showPostSaveOptions, setShowPostSaveOptions,
        showExitOptions, setShowExitOptions,
        procesandoEstado, handleMutarEstadoConsulta, handleFinalizarYSellar,
        navigate, showToast
    };
}